import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { DriveLookupService } from '../../my-account/test-papers/drive-lookup.service';
import { ResourceDownloadService } from '../../common-myaccount-service/resource-download.service';

export interface Resource {
  id: number;
  title: string;
  description: string;
  category: string;
  level: string;
  icon: string;
  colorKey: string;
  fileType: string;
  fileSize: string;
  fileName: string;
  fileUrl: string;
  fileId: string;
  downloads: number;
}

export interface DriveResourceMeta {
  fileName: string;
  title: string;
  description: string;
  category: string;
  level: string;
  icon: string;
  colorKey: string;
  fileType: string;
  fileSize: string;
  downloads: number;
}

@Component({
  selector: 'app-resource-download',
  standalone: true,

  imports: [
    CommonModule,
    FormsModule,
    RouterModule
  ],

  templateUrl: './resource-download.component.html',
  styleUrl: './resource-download.component.css',
})
export class ResourceDownloadComponent implements OnInit {

  // ─────────────────────────────────────────────
  // Resources
  // ─────────────────────────────────────────────

  resources: Resource[] = [];

  filteredResources: Resource[] = [];

  // ─────────────────────────────────────────────
  // Filters
  // ─────────────────────────────────────────────

  categories: string[] = ['All'];

  activeCategory: string = 'All';

  searchQuery: string = '';

  // ─────────────────────────────────────────────
  // Modal
  // ─────────────────────────────────────────────

  showModal = false;

  formSubmitted = false;

  isSubmitting = false;

  selectedResource: Resource | null = null;

  // ─────────────────────────────────────────────
  // Form
  // ─────────────────────────────────────────────

  formData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false,
  };

  formErrors: Record<string, string> = {
    firstName: '',
    lastName: '',
    email: '',
  };

  // ─────────────────────────────────────────────
  // Drive
  // ─────────────────────────────────────────────

  resourcesFolderId = '1scpy6Z_hTujby9WuUeWkOm1BTwuBNnYF';

  apiKey = 'AIzaSyDR484mbUxUuZfrBH2DkmuUrS4oFXhNT9w';

  constructor(
    private drive: DriveLookupService,
    private resourceService: ResourceDownloadService
  ) {}

  ngOnInit(): void {
    this.loadResourcesFromDrive();
  }

  // ─────────────────────────────────────────────
  // Categories
  // ─────────────────────────────────────────────

  buildCategories(): void {

    const uniqueCategories: string[] = [];

    this.resources.forEach((res: Resource) => {

      if (
        res.category &&
        uniqueCategories.indexOf(res.category) === -1
      ) {
        uniqueCategories.push(res.category);
      }

    });

    uniqueCategories.sort();

    this.categories = ['All', ...uniqueCategories];
  }

  // ─────────────────────────────────────────────
  // Filtering
  // ─────────────────────────────────────────────

  setCategory(cat: string): void {

    this.activeCategory = cat;

    this.filterResources();
  }

  filterResources(): void {

    const query = this.searchQuery.toLowerCase().trim();

    this.filteredResources = this.resources.filter((res: Resource) => {

      const matchCat =
        this.activeCategory === 'All' ||
        res.category === this.activeCategory;

      const matchSearch =
        query === '' ||
        res.title.toLowerCase().includes(query) ||
        res.description.toLowerCase().includes(query) ||
        res.category.toLowerCase().includes(query) ||
        res.level.toLowerCase().includes(query);

      return matchCat && matchSearch;

    });

  }

  resetFilters(): void {

    this.activeCategory = 'All';

    this.searchQuery = '';

    this.filteredResources = [...this.resources];

  }

  // ─────────────────────────────────────────────
  // Modal
  // ─────────────────────────────────────────────

  openModal(res: Resource): void {

    this.selectedResource = res;

    this.showModal = true;

    this.formSubmitted = false;

    this.isSubmitting = false;

    this.resetForm();

    document.body.style.overflow = 'hidden';

  }

  closeModal(): void {

    this.showModal = false;

    this.selectedResource = null;

    document.body.style.overflow = '';

  }

  closeAndBrowse(): void {

    this.closeModal();

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

  }

  resetForm(): void {

    this.formData = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      consent: false,
    };

    this.formErrors = {
      firstName: '',
      lastName: '',
      email: '',
    };

  }

  clearError(field: string): void {

    this.formErrors[field] = '';

  }

  // ─────────────────────────────────────────────
  // Validation
  // ─────────────────────────────────────────────

  validate(): boolean {

    let valid = true;

    this.formErrors = {
      firstName: '',
      lastName: '',
      email: '',
    };

    if (!this.formData.firstName.trim()) {

      this.formErrors['firstName'] = 'First name is required';

      valid = false;

    }

    if (!this.formData.lastName.trim()) {

      this.formErrors['lastName'] = 'Last name is required';

      valid = false;

    }

    if (!this.formData.email.trim()) {

      this.formErrors['email'] = 'Email address is required';

      valid = false;

    } else if (!this.isValidEmail(this.formData.email)) {

      this.formErrors['email'] = 'Please enter a valid email address';

      valid = false;

    }

    return valid;

  }

  isValidEmail(email: string): boolean {

    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return re.test(email);

  }

  // ─────────────────────────────────────────────
  // Submit
  // ─────────────────────────────────────────────

  submitForm(): void {

    if (!this.validate()) {
      return;
    }

    if (!this.selectedResource) {
      return;
    }

    this.isSubmitting = true;

    const payload = {

      first_name: this.formData.firstName,

      last_name: this.formData.lastName,

      email: this.formData.email,

      phone: this.formData.phone,

      consent: this.formData.consent ? 1 : 0,

      resource_title: this.selectedResource.title,

      resource_category: this.selectedResource.category,

      file_name: this.selectedResource.fileName,

      download_url: this.selectedResource.fileUrl

    };

    this.resourceService
      .saveLead(payload)
      .subscribe({

        next: (response) => {

          this.isSubmitting = false;

          this.formSubmitted = true;

          const res = this.resources.find(
            r => r.id === this.selectedResource?.id
          );

          if (res) {
            res.downloads += 1;
          }

          console.log('Lead saved successfully', response);

        },

        error: (error) => {

          this.isSubmitting = false;

          console.error('Lead save failed', error);

          alert('Something went wrong. Please try again.');

        }

      });

  }

  onDownloadClick(): void {

    console.log(
      'Download started:',
      this.selectedResource?.title || ''
    );

  }

  // ─────────────────────────────────────────────
  // Google Drive
  // ─────────────────────────────────────────────

  loadResourcesFromDrive(): void {

    this.drive
      .listChildFolders(
        this.resourcesFolderId,
        this.apiKey,
        {
          orderBy: 'name',
        }
      )
      .subscribe({

        next: (categoryFolders: any[]) => {

          if (!categoryFolders?.length) {

            console.log('No category folders found');

            return;

          }

          categoryFolders.forEach((categoryFolder) => {

            this.loadCategoryResources(categoryFolder);

          });

        },

        error: (err) => {

          console.error(
            'Failed to load resource folders',
            err
          );

        },

      });

  }

  loadCategoryResources(categoryFolder: any): void {

    this.drive
      .findFileByExactName(
        categoryFolder.id,
        'resources.json',
        this.apiKey
      )
      .subscribe({

        next: (jsonFile: any) => {

          if (!jsonFile) {

            console.log(
              'resources.json missing in',
              categoryFolder.name
            );

            return;

          }

          this.drive
            .loadJsonFile(
              jsonFile.id,
              this.apiKey
            )
            .subscribe({

              next: (resourceMetaList: DriveResourceMeta[]) => {

                this.drive
                  .listFilesInFolder(
                    categoryFolder.id,
                    this.apiKey
                  )
                  .subscribe({

                    next: (files: any[]) => {

                      this.mapResources(
                        resourceMetaList,
                        files
                      );

                    },

                    error: (err) => {

                      console.error(
                        'Failed to list files',
                        err
                      );

                    },

                  });

              },

              error: (err) => {

                console.error(
                  'Failed to load resources.json',
                  err
                );

              },

            });

        },

        error: (err) => {

          console.error(
            'Failed to find resources.json',
            err
          );

        },

      });

  }

  mapResources(
    resourceMetaList: DriveResourceMeta[],
    files: any[]
  ): void {

    resourceMetaList.forEach((meta) => {

      const file = files.find((f) => {

        return (
          f.name &&
          f.name.toString().trim().toLowerCase() ===
          meta.fileName.toString().trim().toLowerCase()
        );

      });

      if (!file) {

        console.warn(
          'No matching Google Drive file found for:',
          meta.fileName
        );

        return;

      }

      const resource: Resource = {

        id: this.resources.length + 1,

        title: meta.title,

        description: meta.description,

        category: meta.category,

        level: meta.level,

        icon: meta.icon,

        colorKey: meta.colorKey,

        fileType: this.getFileType(meta.fileName),

        fileSize: this.formatBytes(
          Number(file.size || 0)
        ),

        fileName: meta.fileName,

        fileId: file.id,

        fileUrl: this.drive.buildDownloadUrl(file.id),

        downloads: meta.downloads || 0,

      };

      this.resources.push(resource);

    });

    this.filteredResources = [...this.resources];

    this.buildCategories();

  }

  getFileType(fileName: string): string {

    if (!fileName) {
      return 'FILE';
    }

    const parts = fileName.split('.');

    if (parts.length < 2) {
      return 'FILE';
    }

    return parts.pop()?.toUpperCase() || 'FILE';

  }

  formatBytes(bytes: number): string {

    if (!bytes || bytes === 0) {
      return '0 Bytes';
    }

    const k = 1024;

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];

    const i = Math.floor(
      Math.log(bytes) / Math.log(k)
    );

    return (
      parseFloat(
        (bytes / Math.pow(k, i)).toFixed(1)
      ) +
      ' ' +
      sizes[i]
    );

  }

}